/**
 * Arcopedico EU/US Size Switcher — size-switcher.js
 * ===================================================
 * Belle theme (Adorn Themes) specific implementation.
 *
 * Key Belle facts used here:
 * - Size swatches: <input class="swatchInput dn [disabled]"> + <label class="swatchLbl">
 * - Selected swatch: Belle adds orange bg via its own CSS on the checked input's label
 * - Sold-out: "disabled" class on the <input>
 * - Size fieldset: class="product-form__input swatch pvOpt{N}" (N = option position - 1)
 * - Belle's size chart link: <a class="sizelink"> inside the fieldset <legend>
 *
 * Strategy for size chart:
 * - On init, we MOVE Belle's .sizelink anchor out of the fieldset and into our
 *   header row (replacing the placeholder guide link in the snippet).
 * - This keeps it visible in both EU and US modes since we only hide the fieldset body,
 *   not the link itself.
 */

(function () {
  'use strict';

  const SIZE_MAP = {
    35: { women: '4.5-5',   men: null        },
    36: { women: '5.5-6',   men: null        },
    37: { women: '6.5',   men: null        },
    38: { women: '7-7.5',   men: '6'    },
    39: { women: '8-8.5',   men: '6.5-7'    },
    40: { women: '9',   men: '7.5-8'    },
    41: { women: '9.5-10', men: '8.5-9'    },
    42: { women: '10.5-11', men: '9.5'  },
    43: { women: '12', men: '10'  },
    44: { women: '13', men: '10.5'  },
    45: { women: null,       men: '11-11.5'  },
    46: { women: null,       men: '12'  },
  };

  const BELL_SVG = `<svg class="arc-swatch__bell" xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
    stroke-linecap="round" stroke-linejoin="round" width="13" height="13"
    aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>`;

  /* ── Belle DOM helpers ─────────────────────────────────────────────────── */

  function getSizeFieldset() {
    // Size is almost always pvOpt0; fall back to pvOpt1 if Color comes first
    return (
      document.querySelector('fieldset.pvOpt0 label.swatchLbl') ?
        document.querySelector('fieldset.pvOpt0') :
      document.querySelector('fieldset.pvOpt1 label.swatchLbl') ?
        document.querySelector('fieldset.pvOpt1') :
      null
    );
  }

  function getNativeLabels() {
    const fs = getSizeFieldset();
    return fs ? Array.from(fs.querySelectorAll('label.swatchLbl')) : [];
  }

  function getEuValue(label) {
    const input = document.getElementById(label.getAttribute('for'));
    const raw = (input ? input.value : label.textContent).trim();
    const num = parseInt(raw, 10);
    return isNaN(num) ? null : num;
  }

  function isAvailable(label) {
    const input = document.getElementById(label.getAttribute('for'));
    return !!input && !input.classList.contains('disabled');
  }

  function selectNativeEuSize(euSize) {
    getNativeLabels().forEach(label => {
      if (getEuValue(label) === euSize) label.click();
    });
  }

  /* ── Init ──────────────────────────────────────────────────────────────── */

  function init() {
    const switcher = document.getElementById('arc-size-switcher');
    if (!switcher) return;

    const usGrid     = switcher.querySelector('.arc-switcher__us-grid');
    const toggleBtns = switcher.querySelectorAll('.arc-switcher__toggle-btn');
    const guidePlaceholder = switcher.querySelector('.arc-switcher__guide-link');

    /* ── Wire our header link to Belle's size chart ───────────────────────
       Belle's .sizelink has Magnific Popup bound to it on page load.
       Moving the element breaks that listener, so instead we leave the
       original link in place and have our placeholder proxy-click it.
       The original link stays hidden inside the fieldset (which we collapse
       in US mode via visibility:hidden, but a programmatic click still fires).
    ──────────────────────────────────────────────────────────────────────── */
    const belleChartLink = document.querySelector('.sizelink');
    if (belleChartLink && guidePlaceholder) {
      // Match Belle's link text
      guidePlaceholder.textContent = belleChartLink.textContent.trim() || 'Size Chart';
      // Proxy: clicking our link triggers Belle's original link
      guidePlaceholder.addEventListener('click', e => {
        e.preventDefault();
        belleChartLink.click();
      });
    }
    // If no Belle size chart link exists, the placeholder stays as-is (our fallback modal)
 
    /* ── Track selected EU size across mode switches ─────────────────────── */
    let selectedEu = null;
    let currentSystem = 'EU';

    const preSelected = getNativeLabels().find(label => {
      const input = document.getElementById(label.getAttribute('for'));
      return input && input.checked;
    });
    if (preSelected) selectedEu = getEuValue(preSelected);
// Start in US mode
      activateSystem('US');
    /* ── Build US swatch grids ─────────────────────────────────────────── */
    function buildUsGrid() {
      const labels = getNativeLabels();

      ['women', 'men'].forEach(genderKey => {
        const grid = switcher.querySelector(`[data-gender-target="${genderKey}"]`);
        if (!grid) return;
        grid.innerHTML = '';

        labels.forEach(label => {
          const eu = getEuValue(label);
          if (!eu) return;
          const map = SIZE_MAP[eu];
          if (!map) return;
          const usLabel = map[genderKey];
          if (!usLabel) return;

          const available = isAvailable(label);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'arc-swatch' + (available ? '' : ' arc-swatch--oos');
          btn.dataset.eu = eu;
          btn.dataset.gender = genderKey;
          btn.setAttribute('aria-label',
            `US ${genderKey === 'women' ? 'Women' : 'Men'} ${usLabel} (EU ${eu})`);

          if (available) {
            btn.textContent = usLabel;
            btn.addEventListener('click', () => onSwatchClick(btn, eu));
          } else {
            btn.textContent = usLabel;
            btn.setAttribute('aria-disabled', 'true');
            
          }

          grid.appendChild(btn);
        });

        const block = grid.closest('.arc-switcher__gender-block');
        if (block) block.hidden = grid.children.length === 0;
      });

      // Restore selection highlight
      if (selectedEu) {
        switcher.querySelectorAll(`.arc-swatch[data-eu="${selectedEu}"]`).forEach(b => {
          if (!b.classList.contains('arc-swatch--oos')) b.classList.add('arc-swatch--selected');
        });
      }
    }

    function onSwatchClick(clickedBtn, euSize) {
      selectedEu = euSize;
      switcher.querySelectorAll('.arc-swatch--selected').forEach(b => {
        b.classList.remove('arc-swatch--selected');
        b.removeAttribute('aria-pressed');
      });
      clickedBtn.classList.add('arc-swatch--selected');
      clickedBtn.setAttribute('aria-pressed', 'true');
      selectNativeEuSize(euSize);
    }

    /* Keep selectedEu in sync when user clicks a native EU swatch */
    document.addEventListener('change', e => {
      if (e.target.classList.contains('swatchInput')) {
        const eu = parseInt(e.target.value, 10);
        if (!isNaN(eu) && SIZE_MAP[eu] !== undefined) {
          selectedEu = eu;
          if (currentSystem === 'US') {
            switcher.querySelectorAll('.arc-swatch--selected').forEach(b => b.classList.remove('arc-swatch--selected'));
            switcher.querySelectorAll(`.arc-swatch[data-eu="${eu}"]`).forEach(b => {
              if (!b.classList.contains('arc-swatch--oos')) b.classList.add('arc-swatch--selected');
            });
          }
        }
      }
    });

    /* OOS notify stub */
    function openNotifyModal(euSize, genderKey, usLabel) {
      // Replace with your BIS app trigger, e.g. Klaviyo:
      // document.dispatchEvent(new CustomEvent('bis:open', { detail: { variantId: ... } }));
      alert(`Notify me when EU ${euSize} (US ${genderKey === 'women' ? 'Women' : 'Men'} ${usLabel}) is back in stock.`);
    }

    /* ── Toggle EU / US ────────────────────────────────────────────────── */
    function activateSystem(system) {
      currentSystem = system;

      toggleBtns.forEach(btn => {
        const active = btn.dataset.system === system;
        btn.classList.toggle('arc-switcher__toggle-btn--active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      const fs = getSizeFieldset();

      if (system === 'US') {
        // Hide everything inside the fieldset EXCEPT the legend's sizelink
        // (which we already moved out). Hiding the fieldset itself is cleanest.
        if (fs) {
          // Collapse the fieldset. The .sizelink inside it stays in the DOM
          // so programmatic clicks from our proxy link still reach Magnific Popup.
          fs.style.visibility = 'hidden';
          fs.style.height = '0';
          fs.style.overflow = 'hidden';
          fs.style.marginBottom = '0';
        }
        usGrid.hidden = false;
        buildUsGrid();
      } else {
        if (fs) {
          fs.style.visibility = '';
          fs.style.height = '';
          fs.style.overflow = '';
          fs.style.marginBottom = '';
        }
        usGrid.hidden = true;
        // Re-apply EU selection
        if (selectedEu) {
          getNativeLabels().forEach(label => {
            if (getEuValue(label) === selectedEu) {
              const input = document.getElementById(label.getAttribute('for'));
              if (input && !input.checked) label.click();
            }
          });
        }
      }
    }

    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => activateSystem(btn.dataset.system));
    });

    /* ── Our fallback size guide modal (only shown if no Belle .sizelink) ── */
    const sizeGuide = document.getElementById('arc-size-guide');
    const currentGuideLink = switcher.querySelector('.arc-switcher__guide-link');

    // Only wire our modal if there's no Belle size chart link to proxy to
    if (sizeGuide && currentGuideLink && !belleChartLink) {
      currentGuideLink.addEventListener('click', e => {
        e.preventDefault();
        sizeGuide.hidden = false;
        sizeGuide.querySelector('.arc-size-guide__close')?.focus();
      });
      sizeGuide.querySelector('.arc-size-guide__close')?.addEventListener('click', () => {
        sizeGuide.hidden = true;
      });
      sizeGuide.addEventListener('click', e => {
        if (e.target === sizeGuide) sizeGuide.hidden = true;
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !sizeGuide.hidden) sizeGuide.hidden = true;
      });
    }

    /* ── Watch Belle's variant-selects for availability changes ─────────── */
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      new MutationObserver(() => {
        if (currentSystem === 'US') buildUsGrid();
      }).observe(variantSelects, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
